import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Languages, Loader2, MessageCircle, Phone } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { abrirPestanaYa, llevarPestanaA } from '../../lib/citasApi';
import { telefonoConPrefijo } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

/**
 * Enviar un informe médico por WhatsApp, en español o en inglés (administración,
 * 24/9/2026).
 *
 * El mismo mecanismo que el presupuesto: el servidor prepara el mensaje y un
 * enlace al PDF del informe en el idioma elegido, y aquí se abre WhatsApp con
 * todo escrito; la compañera pulsa enviar en WhatsApp.
 *
 * Es un informe médico traducido por máquina, así que quien lo manda lo LEE
 * antes, sin pasos de más: al abrir el diálogo (o cambiar de idioma) se pide la
 * vista previa —ya traducida— y se enseña el teléfono de la ficha, el mensaje y
 * el informe tal y como lo abrirá el paciente. «Enviar» manda exactamente eso:
 * el servidor no vuelve a traducir, y si el informe se ha corregido entre
 * medias, no sale y lo dice.
 *
 * Si algo falla (la traducción, un teléfono que no sirve, un permiso), se dice
 * aquí mismo, en rojo, y no se puede enviar.
 */
export default function EnviarInformeWhatsAppDialog({ informe, open, onOpenChange, onSent }) {
  const [lang, setLang] = useState('es');
  const [vista, setVista] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  // Solo vale la respuesta de la ÚLTIMA petición: si se cambia de idioma con
  // la traducción en el aire, la del idioma anterior no puede pintarse encima.
  const pedida = useRef(0);

  const informeId = informe?.id;

  useEffect(() => {
    if (!open) {
      setLang('es');
      setVista(null);
      setError('');
      setCargando(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !informeId) return undefined;
    const esta = pedida.current + 1;
    pedida.current = esta;
    setVista(null);
    setError('');
    setCargando(true);
    api.get(`/api/informes/${informeId}/whatsapp`, { params: { lang } })
      .then((r) => { if (pedida.current === esta) setVista(r.data); })
      .catch((e) => {
        if (pedida.current !== esta) return;
        setError(mensajeError(e, 'No se ha podido preparar el WhatsApp de este informe.'));
      })
      .finally(() => { if (pedida.current === esta) setCargando(false); });
    return undefined;
  }, [open, informeId, lang]);

  const enviar = async () => {
    if (!vista || enviando) return;
    setEnviando(true);
    setError('');
    // La pestaña se abre en el gesto del usuario y se redirige después
    // (Safari bloquea window.open si el POST tarda), como el presupuesto.
    const ventana = abrirPestanaYa();
    try {
      const r = await api.post(`/api/informes/${informeId}/enviar-whatsapp`, {
        lang: vista.lang,
        titulo: vista.titulo,
        cuerpo: vista.cuerpo,
        huella: vista.huella,
      });
      if (!r.data?.whatsapp_url) {
        ventana?.close();
        setError('El servidor no ha devuelto el enlace de WhatsApp. No se ha enviado nada.');
        return;
      }
      llevarPestanaA(ventana, r.data.whatsapp_url);
      toast.success('WhatsApp preparado', {
        description: 'Pulsa enviar en WhatsApp para que le llegue al paciente.',
      });
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      ventana?.close();
      const texto = mensajeError(e, 'No se ha podido preparar el WhatsApp. No se ha enviado nada.');
      setError(texto);
      toast.error(texto);
    } finally {
      setEnviando(false);
    }
  };

  const traduciendo = cargando && lang === 'en';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!enviando) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="informe-wa-dialog">
        <DialogHeader>
          <DialogTitle>Enviar informe por WhatsApp</DialogTitle>
          <DialogDescription>
            «{informe?.title || 'Informe'}»{informe?.patient_name_snapshot ? ` · ${informe.patient_name_snapshot}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
              <Languages className="w-3.5 h-3.5" /> Idioma del envío
            </div>
            <div className="inline-flex rounded-md border border-border overflow-hidden" role="group">
              {[['es', 'Español'], ['en', 'English']].map(([codigo, nombre]) => (
                <button
                  key={codigo}
                  type="button"
                  onClick={() => setLang(codigo)}
                  disabled={enviando}
                  aria-pressed={lang === codigo}
                  className={`px-3 py-1.5 text-sm ${lang === codigo
                    ? 'bg-[#2E6E5E] text-white'
                    : 'bg-white text-[#13231E] hover:bg-[rgba(46,110,94,0.06)]'}`}
                  data-testid={`informe-wa-lang-${codigo}`}
                >
                  {nombre}
                </button>
              ))}
            </div>
          </div>

          {cargando && (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="informe-wa-cargando">
              <Loader2 className="w-4 h-4 mr-1.5 inline animate-spin" />
              {traduciendo ? 'Traduciendo el informe al inglés…' : 'Preparando el envío…'}
            </div>
          )}

          {!cargando && error && (
            <div
              role="alert"
              className="text-sm rounded-md bg-red-50 text-red-900 border border-red-200 px-3 py-2 flex gap-2"
              data-testid="informe-wa-error"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!cargando && vista && (
            <>
              <div className="text-sm flex items-center gap-1.5" data-testid="informe-wa-telefono">
                <Phone className="w-4 h-4 text-[#2E6E5E]" />
                Se enviará al <strong className="tabular-nums">{telefonoConPrefijo(vista.telefono)}</strong>
                <span className="text-muted-foreground">(teléfono de la ficha)</span>
              </div>

              {vista.traducido && (
                <p
                  className="text-xs rounded bg-amber-50 text-amber-900 border border-amber-200 px-2 py-1.5"
                  data-testid="informe-wa-aviso-traduccion"
                >
                  Traducción automática. Léela antes de enviarla: las cifras, dosis, fechas,
                  nombres y fármacos se copian tal cual del original. Si algo no está bien,
                  corrige el informe en español y vuelve a abrir el envío.
                </p>
              )}

              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Mensaje de WhatsApp</div>
                <pre
                  className="whitespace-pre-wrap break-words text-sm font-sans rounded-md border border-border bg-[#F0F6F2] p-3"
                  data-testid="informe-wa-mensaje"
                >
                  {vista.mensaje}
                </pre>
              </div>

              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  Informe que abrirá el paciente (PDF{vista.traducido ? ', en inglés' : ''})
                </div>
                <div
                  className="rounded-md border border-border p-3 max-h-80 overflow-y-auto"
                  data-testid="informe-wa-vista"
                >
                  <div className="font-semibold text-sm mb-2">{vista.titulo}</div>
                  <pre className="whitespace-pre-wrap break-words text-sm font-sans">{vista.cuerpo}</pre>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={enviando}
            data-testid="informe-wa-cancelar"
          >
            Cancelar
          </Button>
          <Button
            onClick={enviar}
            disabled={enviando || cargando || !vista || !!error}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="informe-wa-enviar"
          >
            {enviando
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <MessageCircle className="w-4 h-4 mr-1.5" />}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
